import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Plus, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Role } from '@nexa/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RoleGate } from '@/auth/role-gate';
import { useContracts } from '@/lib/contracts/use-contracts';
import type { Contract, ContractStatus } from '@/lib/contracts/types';

// /contracts route per docs/ui/operator-ui-tds.md §4.1 + §8.a8.
// Filters live in the URL so the list is shareable (?airport=SCL&status=active).

export function ContractsRoute() {
  const { t } = useTranslation('network');
  const [search, setSearch] = useSearchParams();
  const airport = search.get('airport') ?? '';
  const status = (search.get('status') as ContractStatus | null) ?? undefined;
  const expiringWithinDays = Number(search.get('expiringWithinDays') ?? '') || undefined;

  const list = useContracts({
    ...(airport ? { airport } : {}),
    ...(status ? { status } : {}),
    ...(expiringWithinDays ? { expiringWithinDays } : {}),
  });

  const patch = (key: string, value: string) => {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearch(next, { replace: true });
  };

  return (
    <RoleGate min={Role.OPS_SUPERVISOR} hideWhenDenied>
      <main className="density-page mx-auto flex max-w-5xl flex-col gap-4 p-6">
        <header className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-semibold">{t('common:nav.contracts')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('contracts.list.subtitle')}</p>
          </div>
          <Button asChild>
            <Link to="/contracts/new">
              <Plus className="mr-1 h-3 w-3" aria-hidden />
              {t('contracts.list.new')}
            </Link>
          </Button>
        </header>

        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium text-foreground/80">{t('contracts.list.airport')}</span>
            <input
              value={airport}
              onChange={(e) => patch('airport', e.target.value.toUpperCase())}
              maxLength={3}
              placeholder="SCL"
              className="w-20 rounded-md border border-input bg-background px-2 py-1 text-sm uppercase"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium text-foreground/80">{t('contracts.list.status')}</span>
            <select
              value={status ?? ''}
              onChange={(e) => patch('status', e.target.value)}
              className="rounded-md border border-input bg-background px-2 py-1 text-sm"
            >
              <option value="">{t('contracts.list.statusAll')}</option>
              <option value="active">{t('contracts.list.statusActive')}</option>
              <option value="inactive">{t('contracts.list.statusInactive')}</option>
              <option value="expired">{t('contracts.list.statusExpired')}</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium text-foreground/80">
              {t('contracts.list.expiringWithin')}
            </span>
            <input
              type="number"
              min={0}
              value={expiringWithinDays ?? ''}
              onChange={(e) => patch('expiringWithinDays', e.target.value)}
              className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm"
            />
          </label>
        </div>

        {list.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : list.isError ? (
          <Card>
            <CardContent className="p-6 text-sm text-destructive">
              {t('contracts.list.loadError')}
            </CardContent>
          </Card>
        ) : (list.data ?? []).length === 0 ? (
          <EmptyState
            title={t('contracts.list.empty')}
            description={t('contracts.list.emptyHint')}
          />
        ) : (
          <ul className="space-y-2" data-testid="contracts-list">
            {(list.data ?? []).map((c) => (
              <li key={c.contractUrn}>
                <Card>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/contracts/${encodeURIComponent(c.contractUrn)}`}
                          className="text-base font-medium text-primary underline-offset-2 hover:underline"
                        >
                          {c.hotelProviderName}
                        </Link>
                        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                          {c.airportCode}
                        </span>
                        <StatusPill status={c.status} />
                        {c.status === 'active' && <EligibilityPill contract={c} />}
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t('contracts.list.rate', {
                          currency: c.currency,
                          rate: c.ratePerNight,
                          rooms: c.guaranteedRooms,
                          stars: c.minStars,
                        })}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {t('contracts.list.valid', {
                          from: new Date(c.validFrom).toLocaleDateString(),
                          until: new Date(c.validUntil).toLocaleDateString(),
                        })}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </main>
    </RoleGate>
  );
}

function StatusPill({ status }: { status: ContractStatus }) {
  const { t } = useTranslation('network');
  const cls =
    status === 'active'
      ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100'
      : status === 'expired'
      ? 'bg-destructive/10 text-destructive'
      : 'bg-muted text-muted-foreground';
  return (
    <span className={`rounded px-1.5 py-0.5 text-xs uppercase ${cls}`}>
      {t(`contracts.status.${status}`)}
    </span>
  );
}

function EligibilityPill({ contract }: { contract: Contract }) {
  const { t } = useTranslation('network');
  const eligibility = contract.policyEligibility;

  if (!eligibility || !eligibility.policyConstraints) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              {t('contracts.eligibility.policyNotResolved')}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs text-xs">{t('contracts.eligibility.noConstraints')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (eligibility.eligible) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100">
        <CheckCircle2 className="h-3 w-3" aria-hidden />
        {t('contracts.eligibility.eligible')}
      </span>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
            <AlertTriangle className="h-3 w-3" aria-hidden />
            {t('contracts.eligibility.notEligible')}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <div className="max-w-sm space-y-1 text-xs">
            <p className="font-medium">{t('contracts.eligibility.violation')}</p>
            {eligibility.violations.map((v, i) => (
              <p key={i} className="text-muted-foreground">
                {t('contracts.eligibility.violationDetail', { violation: v })}
              </p>
            ))}
            {eligibility.policyConstraints?.minStars && (
              <p className="mt-1 text-muted-foreground">
                {t('contracts.eligibility.minStarsNote', {
                  stars: eligibility.policyConstraints.minStars,
                })}
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
